import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useOfferCartVariables from '../../../hooks/useOfferCartVariables';
import ShoppingCartStepper from '../ShoppingCartStepper';
import buildCessionTypeSelectorConfiguration from './config/steps-configurations/cession-type-selector-configuration';
import buildConfirmationConfiguration from './config/steps-configurations/confirmation-configuration';
import buildCredentialProviderCredentialsConfiguration from './config/steps-configurations/credential-provider-credentials-configuration';
import buildCredentialProviderSelectorConfiguration from './config/steps-configurations/credential-provider-selector-configuration';
import buildExternalCessionBankAccountConfiguration from './config/steps-configurations/external-cession-bank-account-configuration';
import buildExternalCessionInstructionsConfiguration from './config/steps-configurations/external-cession-instructions-configuration';
import buildSelectBankAccountConfiguration from './config/steps-configurations/select-bank-account-configuration';
import buildSummaryStepConfiguration from './config/steps-configurations/summary-configuration';
import buildUploadCertificateConfiguration from './config/steps-configurations/upload-certificate-configuration';
import buildUploadDocumentsConfiguration from './config/steps-configurations/upload-documents-configuration';

const OfferShoppingCart = ({
  toggleMenu,
  useSummaryStep,
  WarningComponent,
  warningComponentProps,
  onRegisterNavigation,
  showStepper,
  requireBankAccount,
}) => {
  const { t } = useTranslation();
  const {
    hasDigitalCertificate,
    selectedProvider,
    setSelectedProvider,
    selectedCessionType,
    setSelectedCessionType,
    data,
    loading,
    invoices,
    allDocumentsHaveXML,
    shouldShowCredentialStep,
    shouldShowUploadDocumentsStep,
    certificatePassword,
    setCertificatePassword,
  } = useOfferCartVariables();
  const steps = useMemo(() => {
    if (!data) return [];
    const stepsArray = [
      ...(useSummaryStep ? [buildSummaryStepConfiguration()] : []),
      buildCredentialProviderSelectorConfiguration({
        selectedProvider,
        setSelectedProvider,
      }),
      buildCessionTypeSelectorConfiguration({
        t,
        selectedCessionType,
        setSelectedCessionType,
        selectedProvider,
        allDocumentsHaveXML,
      }),
    ];

    if (shouldShowCredentialStep) {
      stepsArray.push(buildCredentialProviderCredentialsConfiguration({
        selectedProvider,
      }));
    }

    if (shouldShowUploadDocumentsStep) {
      stepsArray.push(buildUploadDocumentsConfiguration({ invoices }));
    }

    if (selectedCessionType === 'internal') {
      stepsArray.push(buildUploadCertificateConfiguration({
        hasDigitalCertificate,
        selectedProvider,
        onCertificateUploaded: ({ password }) => setCertificatePassword(password),
        requireBankAccount,
      }));
      if (requireBankAccount) {
        stepsArray.push(buildSelectBankAccountConfiguration({
          certificatePassword,
        }));
      }
    } else {
      stepsArray.push(buildExternalCessionInstructionsConfiguration({
        requireBankAccount,
      }));
      if (requireBankAccount) {
        stepsArray.push(buildExternalCessionBankAccountConfiguration());
      }
    }

    stepsArray.push(buildConfirmationConfiguration({
      toggleMenu,
      WarningComponent,
      warningComponentProps,
    }));

    return stepsArray;
  }, [
    shouldShowCredentialStep,
    selectedProvider,
    selectedCessionType,
    data,
    certificatePassword,
    shouldShowUploadDocumentsStep,
    requireBankAccount,
    hasDigitalCertificate,
    invoices,
    allDocumentsHaveXML,
    useSummaryStep,
    t,
    toggleMenu,
    WarningComponent,
    warningComponentProps,
  ]);

  return (
    <ShoppingCartStepper
      steps={steps}
      loading={loading}
      onRegisterNavigation={onRegisterNavigation}
      showStepper={showStepper}
    />
  );
};

OfferShoppingCart.propTypes = {
  toggleMenu: PropTypes.func.isRequired,
  useSummaryStep: PropTypes.bool,
  WarningComponent: PropTypes.elementType,
  warningComponentProps: PropTypes.shape({}),
  onRegisterNavigation: PropTypes.func,
  showStepper: PropTypes.bool,
  requireBankAccount: PropTypes.bool,
};

OfferShoppingCart.defaultProps = {
  useSummaryStep: true,
  WarningComponent: null,
  warningComponentProps: {},
  onRegisterNavigation: () => {},
  showStepper: true,
  requireBankAccount: true,
};

export default React.memo(OfferShoppingCart);
